import { useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { BUILTIN_FIELD_TYPE_SPECS, Member, MemberSearchResult, getFieldTypeSpec, page, validateFieldValue } from '@cloudsign/shared';
import type { Field, FieldOption, FieldWidget } from '@cloudsign/shared';
import { api } from '../lib/api';
import { t } from '../lib/i18n';
import { CheckboxField, SelectField, TextField } from '../components/ui';
import { NoticeView } from './fields-notices/noticeText';
import { SignaturePad } from './SignaturePad';

/**
 * One question drawn from the shared field-type registry (packages/shared/src/fields), used wherever the portal asks
 * a custom or personal field: the Today sign-in dialog and the member form. The registry says which widget a type
 * uses, what its config means and whether an answer is valid, so the portal never keeps its own list of types.
 *
 * The "raw" value is what the input holds while the person types (a number is still a string, a yes/no is a boolean
 * or undefined while unanswered). `valueFromRaw` turns it into the value the server stores.
 */

export type Raw = unknown;
export interface HostValue {
  memberId: string;
  name: string;
}

const MemberPage = page(Member);
const widgetOf = (field: Field): FieldWidget => getFieldTypeSpec(field.typeKey)?.widget ?? 'text';

/** The choices of a select / multi-select question, read with the type's own config schema. */
export function optionsOf(field: Field): FieldOption[] {
  const spec = getFieldTypeSpec(field.typeKey);
  const cfg = spec?.configSchema.safeParse(field.config ?? {});
  const options = cfg?.success ? (cfg.data as { options?: unknown }).options : undefined;
  return Array.isArray(options) ? (options as FieldOption[]) : [];
}

/** True for the built-in or module-added types the portal knows how to draw. */
export const isKnownType = (typeKey: string): boolean => BUILTIN_FIELD_TYPE_SPECS.some((s) => s.key === typeKey);

/** A stored value as the input holds it. */
export function rawFromValue(field: Field, value: unknown): Raw {
  switch (widgetOf(field)) {
    case 'multi_select':
      return Array.isArray(value) ? value.map(String) : [];
    case 'yes_no':
    case 'acknowledge':
      return typeof value === 'boolean' ? value : undefined;
    case 'member_picker':
    case 'signature':
      return value && typeof value === 'object' ? value : undefined;
    default:
      return value === undefined || value === null ? '' : String(value);
  }
}

/** What the input holds, as the value to send. undefined means "no answer". */
export function valueFromRaw(field: Field, raw: Raw): unknown {
  switch (widgetOf(field)) {
    case 'number':
      return raw === '' || raw === undefined || raw === null ? undefined : Number(raw);
    case 'multi_select':
      return Array.isArray(raw) && raw.length > 0 ? raw : undefined;
    case 'yes_no':
      return typeof raw === 'boolean' ? raw : undefined;
    case 'acknowledge':
      return raw === true ? true : undefined;
    case 'member_picker':
    case 'signature':
      return raw && typeof raw === 'object' ? raw : undefined;
    default:
      return typeof raw === 'string' && raw.trim() !== '' ? raw : undefined;
  }
}

export type AnswerCheck = { ok: true; value: unknown } | { ok: false; messageKey: string };

/** Checks one answer with the registry. `value` is null when the question is optional and left empty. */
export function checkAnswer(field: Field, raw: Raw): AnswerCheck {
  const value = valueFromRaw(field, raw);
  const spec = getFieldTypeSpec(field.typeKey);
  if (!spec) return field.required && value === undefined ? { ok: false, messageKey: 'error.field_required' } : { ok: true, value: value ?? null };
  return validateFieldValue(spec, field.config, value, field.required);
}

function MemberPick({
  label,
  plainLabel,
  hint,
  error,
  groupIds,
  siteId,
  value,
  onChange,
}: {
  label: string;
  plainLabel: string;
  hint: string | undefined;
  error: string | undefined;
  groupIds: string[];
  siteId: string | undefined;
  value: HostValue | undefined;
  onChange: (v: HostValue | undefined) => void;
}) {
  const [term, setTerm] = useState('');
  const results = useQueries({
    queries: groupIds.map((groupId) => ({
      queryKey: ['field-input', 'members', siteId ?? null, groupId, term],
      // At a site (the Today sign-in dialog) the narrow sign-in search is used: it needs only today.sign_in_out.
      queryFn: () =>
        siteId
          ? api.get('/visits/member-search', { query: { siteId, groupId, search: term.trim(), pageSize: 5 }, schema: MemberSearchResult })
          : api.get('/members', { query: { groupId, search: term, pageSize: 5 }, schema: MemberPage }),
      enabled: term.trim().length > 1 && !value,
    })),
  });
  const found = results.flatMap((q) => q.data?.items ?? []);
  if (value) {
    return (
      <p className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-sm">
        <span>{t('fieldInput.chosen', { label: plainLabel, name: value.name })}</span>
        <button type="button" className="text-brand underline" onClick={() => onChange(undefined)}>
          {t('fieldInput.change')}
        </button>
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <TextField label={label} hint={hint ?? t('fieldInput.memberHint')} error={error} value={term} onChange={(e) => setTerm(e.target.value)} />
      {found.length > 0 ? (
        <ul aria-label={t('fieldInput.memberResults', { label: plainLabel })} className="max-h-40 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200">
          {found.map((m) => (
            <li key={m.id}>
              <button type="button" className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={() => onChange({ memberId: m.id, name: m.fullName })}>
                {m.fullName}
                {(m as { role?: string | null }).role ? <span className="ml-2 text-slate-500">{(m as { role?: string | null }).role}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function FieldInput({
  field,
  raw,
  onChange,
  error,
  repeatGroupIds = [],
  siteId,
}: {
  field: Field;
  raw: Raw;
  onChange: (v: Raw) => void;
  /** An i18n key (for example error.field_required) or already-translated text. */
  error?: string | undefined;
  /** Groups a host question searches when its own config names none. */
  repeatGroupIds?: string[];
  /** Set where the person is signing in at a site (Today dialog): host search uses the narrow route and a signature can be drawn. */
  siteId?: string;
}) {
  const spec = getFieldTypeSpec(field.typeKey);
  const widget = widgetOf(field);
  const label = field.required ? `${field.label} *` : field.label;
  const hint = field.helpText || undefined;
  const err = error ? (error.startsWith('error.') ? t(error) : error) : undefined;
  const webInput = spec?.hints?.web.input ?? 'text';
  const str = typeof raw === 'string' ? raw : '';

  switch (widget) {
    case 'textarea':
      return (
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-800" htmlFor={`q-${field.id}`}>
            {label}
          </label>
          <textarea
            id={`q-${field.id}`}
            rows={3}
            className="block w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
            aria-invalid={err ? true : undefined}
            value={str}
            onChange={(e) => onChange(e.target.value)}
          />
          {hint ? <p className="mt-1 text-xs text-slate-600">{hint}</p> : null}
          {err ? <p className="mt-1 text-sm text-red-700">{err}</p> : null}
        </div>
      );
    case 'select':
      return (
        <SelectField label={label} hint={hint} error={err} value={str} onChange={(e) => onChange(e.target.value)}>
          <option value="">{t('fieldInput.choose')}</option>
          {optionsOf(field).map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </SelectField>
      );
    case 'multi_select': {
      const chosen = Array.isArray(raw) ? (raw as string[]) : [];
      return (
        <fieldset>
          <legend className="mb-1 text-sm font-medium text-slate-800">{label}</legend>
          {hint ? <p className="mb-1 text-xs text-slate-600">{hint}</p> : null}
          <div className="space-y-1">
            {optionsOf(field).map((o) => (
              <CheckboxField
                key={o.id}
                label={o.label}
                checked={chosen.includes(o.id)}
                onChange={(e) => onChange(e.target.checked ? [...chosen, o.id] : chosen.filter((x) => x !== o.id))}
              />
            ))}
          </div>
          {err ? <p className="mt-1 text-sm text-red-700">{err}</p> : null}
        </fieldset>
      );
    }
    case 'yes_no':
      return (
        <fieldset>
          <legend className="mb-1 text-sm font-medium text-slate-800">{label}</legend>
          {hint ? <p className="mb-1 text-xs text-slate-600">{hint}</p> : null}
          <div className="flex gap-6">
            {[true, false].map((answer) => (
              <label key={String(answer)} className="inline-flex items-center gap-2 text-sm">
                <input type="radio" name={`q-${field.id}`} className="h-5 w-5 accent-[var(--brand-primary)]" checked={raw === answer} onChange={() => onChange(answer)} />
                {answer ? t('common.yes') : t('common.no')}
              </label>
            ))}
          </div>
          {err ? <p className="mt-1 text-sm text-red-700">{err}</p> : null}
        </fieldset>
      );
    case 'acknowledge': {
      const message = typeof field.config['message'] === 'string' ? field.config['message'] : '';
      return (
        <div className="space-y-2">
          {message ? (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <NoticeView body={message} />
            </div>
          ) : null}
          <CheckboxField label={label} hint={hint} checked={raw === true} onChange={(e) => onChange(e.target.checked ? true : undefined)} />
          {err ? <p className="text-sm text-red-700">{err}</p> : null}
        </div>
      );
    }
    case 'signature':
      if (siteId) {
        return <SignaturePad label={label} siteId={siteId} value={raw && typeof raw === 'object' ? (raw as { fileId: string }) : undefined} error={err} onChange={onChange} />;
      }
      return (
        <div className="rounded-md border border-dashed border-slate-300 p-3">
          <p className="text-sm font-medium text-slate-800">{label}</p>
          <p className="mt-1 text-sm text-slate-600">{t('fieldInput.signatureAtKiosk')}</p>
          {err ? <p className="mt-1 text-sm text-red-700">{err}</p> : null}
        </div>
      );
    case 'member_picker': {
      const named = Array.isArray(field.config['groupIds']) ? (field.config['groupIds'] as string[]) : [];
      return (
        <MemberPick
          label={label}
          plainLabel={field.label}
          hint={hint}
          error={err}
          groupIds={named.length > 0 ? named : repeatGroupIds}
          siteId={siteId}
          value={raw && typeof raw === 'object' ? (raw as HostValue) : undefined}
          onChange={onChange}
        />
      );
    }
    default: {
      const type = webInput === 'tel' || webInput === 'email' || webInput === 'number' || webInput === 'date' ? webInput : 'text';
      const config = field.config as { integer?: boolean; unit?: string };
      return (
        <TextField
          label={config.unit ? `${label} (${config.unit})` : label}
          hint={hint}
          error={err}
          type={type}
          {...(type === 'number' ? { step: config.integer ? 1 : 'any' } : {})}
          value={str}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    }
  }
}

